import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Star, ShieldCheck, Zap, HeartHandshake, ChevronDown, Info } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ChineseServiceRecommendationEngine } from "./ChineseServiceRecommendationEngine";
import { useLocation } from "@/lib/locations";
import { getLocalContent } from "@/lib/city-content";
import { useCalculatorState } from "@/hooks/use-calculator-state";

const BOOKING_URL = "/book";

export const ChineseServices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { update } = useCalculatorState();
  const localContent = getLocalContent(location.name, location.regionSlug, 'zh');
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.02 });

  const handleBookWithService = (service: 'standard' | 'deep' | 'move') => {
    update({ serviceType: service });
    navigate(`${BOOKING_URL}?view=custom-plan`);
  };

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-50" id="services">
      <div className="container" ref={sectionRef}>
        
        {/* HERO TOP */}
        <div className={cn("grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center mb-16 anim-fade-up", sectionVisible && "is-visible")}>
          <div>
            <div className="text-primary dark:text-slate-900 font-bold tracking-widest uppercase text-xs mb-4">超越清洁</div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-foreground dark:text-slate-900">
              为您的家选择合适的清洁服务
            </h2>
            <p className="text-lg text-muted-foreground dark:text-slate-600 max-w-2xl mb-4">
              简单、优质的清洁选项，60秒内即可预订。200%满意度保证。
            </p>
            <p className="text-base text-muted-foreground dark:text-slate-600 max-w-2xl mb-8 border-l-4 border-primary pl-4">
              {localContent.localIssuesText}
            </p>
            <Button asChild size="lg" className="rounded-full px-8 text-base shadow-elegant">
              <a href={`${BOOKING_URL}?view=custom-plan`}>
                <Zap className="mr-2 h-4 w-4 fill-primary/20" />
                获取免费报价 
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>

          <div className="bg-gradient-to-br from-white to-blue-50 dark:from-white dark:to-blue-50 p-8 rounded-3xl shadow-elegant border border-blue-100 dark:border-slate-200">
            <div className="grid grid-cols-2 gap-4">
              {/* Star rating box removed */}
              <div className="bg-white dark:bg-white p-4 rounded-2xl font-bold text-center flex flex-col items-center gap-2 shadow-sm border border-slate-100 dark:border-slate-200">
                <ShieldCheck className="h-6 w-6 text-primary dark:text-slate-900" />
                <span className="text-sm dark:text-slate-900">全面保险</span>
              </div>
              <div className="bg-white dark:bg-white p-4 rounded-2xl font-bold text-center flex flex-col items-center gap-2 shadow-sm border border-slate-100 dark:border-slate-200">
                <Zap className="h-6 w-6 text-orange-400 fill-orange-400" />
                <span className="text-sm dark:text-slate-900">快速预订</span>
              </div>
              <div className="bg-white dark:bg-white p-4 rounded-2xl font-bold text-center flex flex-col items-center gap-2 shadow-sm border border-slate-100 dark:border-slate-200">
                <HeartHandshake className="h-6 w-6 text-red-400 fill-red-400" />
                <span className="text-sm dark:text-slate-900">满意保证</span>
              </div>
            </div>
          </div>
        </div>


        <div className={cn("mb-16 bg-white dark:bg-white border border-border dark:border-slate-200 rounded-3xl p-8 shadow-sm anim-fade-up", sectionVisible && "is-visible")}>
          <div className="text-primary dark:text-slate-900 font-bold tracking-widest uppercase text-xs mb-2">快速比较</div>
          <h3 className="text-2xl font-bold mb-6 dark:text-slate-900">哪个套餐适合您？</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <button className="bg-slate-50 dark:bg-slate-50 p-4 rounded-2xl text-left hover:bg-slate-100 dark:hover:bg-slate-100 transition-colors group relative border border-transparent dark:border-slate-200">
                  <strong className="block text-foreground dark:text-slate-900 mb-1 flex items-center justify-between">
                    标准清洁
                    <Info className="h-3.5 w-3.5 text-muted-foreground dark:text-slate-600 group-hover:text-primary transition-colors" />
                  </strong>
                  <span className="text-sm text-muted-foreground dark:text-slate-600">日常维护与刷新</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-5 rounded-2xl shadow-xl border-border dark:border-slate-200 bg-white dark:bg-white">
                <div className="space-y-3">
                  <h4 className="font-bold text-primary dark:text-slate-900 flex items-center gap-2">
                    <Zap className="h-4 w-4 fill-primary/20 dark:fill-slate-900/20" /> 标准清洁包含：
                  </h4>
                  <ul className="grid grid-cols-1 gap-2 text-xs text-muted-foreground dark:text-slate-600">
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 厨房表面和台面</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 浴室（马桶、洗手盆、淋浴）</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 擦拭可触及表面灰尘</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 吸尘和拖地</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 家电外部清洁</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 垃圾清理</li>
                  </ul>
                  <Button variant="link" className="p-0 h-auto text-xs font-bold text-primary dark:text-slate-900" onClick={() => handleBookWithService('standard')}>
                    选择标准清洁并获取价格 →
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button className="bg-slate-50 dark:bg-slate-50 p-4 rounded-2xl text-left hover:bg-slate-100 dark:hover:bg-slate-100 transition-colors group relative border border-transparent dark:border-slate-200">
                  <strong className="block text-foreground dark:text-slate-900 mb-1 flex items-center justify-between">
                    深度清洁
                    <Info className="h-3.5 w-3.5 text-muted-foreground dark:text-slate-600 group-hover:text-primary transition-colors" />
                  </strong>
                  <span className="text-sm text-muted-foreground dark:text-slate-600">首次或长期未清洁</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-5 rounded-2xl shadow-xl border-border dark:border-slate-200 bg-white dark:bg-white">
                <div className="space-y-3">
                  <h4 className="font-bold text-primary dark:text-slate-900 flex items-center gap-2">
                    <Zap className="h-4 w-4 fill-primary/20 dark:fill-slate-900/20" /> 深度清洁包含：
                  </h4>
                  <p className="text-[11px] font-bold text-muted-foreground dark:text-slate-600 uppercase tracking-wider bg-slate-100 px-2 py-1 rounded">包含标准清洁所有内容 +</p>
                  <ul className="grid grid-cols-1 gap-2 text-xs text-muted-foreground dark:text-slate-600">
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 踢脚线（手工擦拭）</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 门框和灯开关</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 浴室积垢详细清除</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 厨房油垢清除</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 难以触及的区域</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 家电外部细节清洁</li>
                  </ul>
                  <Button variant="link" className="p-0 h-auto text-xs font-bold text-primary dark:text-slate-900" onClick={() => handleBookWithService('deep')}>
                    选择深度清洁并获取价格 →
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button className="bg-slate-50 dark:bg-slate-50 p-4 rounded-2xl text-left hover:bg-slate-100 dark:hover:bg-slate-100 transition-colors group relative border border-transparent dark:border-slate-200">
                  <strong className="block text-foreground dark:text-slate-900 mb-1 flex items-center justify-between">
                    搬家清洁
                    <Info className="h-3.5 w-3.5 text-muted-foreground dark:text-slate-600 group-hover:text-primary transition-colors" />
                  </strong>
                  <span className="text-sm text-muted-foreground dark:text-slate-600">空房重置</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-5 rounded-2xl shadow-xl border-border dark:border-slate-200 bg-white dark:bg-white">
                <div className="space-y-3">
                  <h4 className="font-bold text-primary dark:text-slate-900 flex items-center gap-2">
                    <Zap className="h-4 w-4 fill-primary/20 dark:fill-slate-900/20" /> 搬家清洁包含：
                  </h4>
                  <p className="text-[11px] font-bold text-muted-foreground dark:text-slate-600 uppercase tracking-wider bg-slate-100 px-2 py-1 rounded">包含深度清洁所有内容 +</p>
                  <ul className="grid grid-cols-1 gap-2 text-xs text-muted-foreground dark:text-slate-600">
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 橱柜和抽屉内部</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 家电内部（烤箱、冰箱）</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 厨房和浴室全面重置</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 空置空间的灰尘和碎片清理</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-slate-900" /> 达到退还押金标准</li>
                  </ul>
                  <Button variant="link" className="p-0 h-auto text-xs font-bold text-primary dark:text-slate-900" onClick={() => handleBookWithService('move')}>
                    选择搬家清洁并获取价格 →
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <div className="bg-primary/10 border border-primary/20 p-4 rounded-2xl">
              <strong className="block text-primary dark:text-slate-900 mb-1">定期清洁</strong>
              <span className="text-sm text-muted-foreground dark:text-slate-600">最大节省与一致性</span>
            </div>
          </div>
        </div>

        {/* PACKAGES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          
          {/* STANDARD */}
          <div className={cn("bg-white dark:bg-white border border-border dark:border-slate-200 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col anim-fade-up", sectionVisible && "is-visible")}>
            <h3 className="text-2xl font-bold mb-2 dark:text-slate-900">标准清洁</h3>
            <p className="text-muted-foreground dark:text-slate-600 text-sm mb-6 min-h-[40px]">为您的家进行日常维护。</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 厨房和浴室</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 地板和除尘</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 表面擦拭</li>
              <li className="flex items-center gap-3 text-sm dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 整理床铺和垃圾</li>
            </ul>
            <Button variant="outline" className="w-full rounded-xl border-border dark:border-slate-200 hover:bg-secondary dark:text-slate-900" onClick={() => handleBookWithService('standard')}>
              <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-slate-900/20" />
              获取即时报价
            </Button>
            <Link to="/zh/services/standard-clean" className="text-xs text-center text-muted-foreground dark:text-slate-600 hover:text-primary dark:hover:text-slate-900 mt-4 block">了解更多关于标准清洁</Link>
          </div>

          {/* DEEP CLEAN */}
          <div className={cn("bg-white dark:bg-white border border-border dark:border-slate-200 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col relative anim-fade-up", sectionVisible && "is-visible")}>
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-slate-800 text-white font-black text-xs sm:text-sm uppercase tracking-widest px-6 py-2 rounded-full shadow-xl ring-4 ring-background whitespace-nowrap">
              首次清洁的最佳选择
            </div>
            <h3 className="text-2xl font-bold mb-2 mt-2 dark:text-slate-900">深度清洁</h3>
            <p className="text-muted-foreground dark:text-slate-600 text-sm mb-6 min-h-[40px]">首次服务或需要额外护理。</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 包含标准清洁所有内容</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 踢脚线和饰面</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 去除顽固污垢</li>
              <li className="flex items-center gap-3 text-sm dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 详细擦洗</li>
            </ul>
            <Button variant="outline" className="w-full rounded-xl border-border dark:border-slate-200 hover:bg-secondary dark:text-slate-900" onClick={() => handleBookWithService('deep')}>
              <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-slate-900/20" />
              获取即时报价
            </Button>
            <Link to="/zh/services/deep-clean" className="text-xs text-center text-muted-foreground dark:text-slate-600 hover:text-primary dark:hover:text-slate-900 mt-4 block">了解更多关于深度清洁</Link>
          </div>

          {/* MOVE IN/OUT */}
          <div className={cn("bg-white dark:bg-white border border-border dark:border-slate-200 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col anim-fade-up", sectionVisible && "is-visible")}>
            <h3 className="text-2xl font-bold mb-2 dark:text-slate-900">搬家清洁</h3>
            <p className="text-muted-foreground dark:text-slate-600 text-sm mb-6 min-h-[40px]">轻松的空房搬家清洁。</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 橱柜内部</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 烤箱和冰箱</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-200 dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 押金退还标准</li>
              <li className="flex items-center gap-3 text-sm dark:text-slate-900"><Check className="h-4 w-4 text-primary dark:text-slate-900 shrink-0" /> 空房重置</li>
            </ul>
            <Button variant="outline" className="w-full rounded-xl border-border dark:border-slate-200 hover:bg-secondary dark:text-slate-900" onClick={() => handleBookWithService('move')}>
              <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-slate-900/20" />
              获取即时报价
            </Button>
            <Link to="/zh/services/move-in-out" className="text-xs text-center text-muted-foreground dark:text-slate-600 hover:text-primary dark:hover:text-slate-900 mt-4 block">了解更多关于搬家清洁</Link>
          </div>

          {/* RECURRING (FEATURED) */}
          <div className={cn("bg-primary text-primary-foreground rounded-3xl p-8 shadow-elegant hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col relative anim-fade-up", sectionVisible && "is-visible")}>
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground font-black text-xs sm:text-sm uppercase tracking-widest px-6 py-2 rounded-full shadow-xl shadow-accent/30 ring-4 ring-background whitespace-nowrap">
              节省 20-35%
            </div>
            <h3 className="text-2xl font-bold mb-2 mt-2">定期清洁</h3>
            <p className="text-primary-foreground/80 text-sm mb-6 min-h-[40px]">每周 / 每两周 / 每月定期清洁。</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-primary-foreground/20"><Check className="h-4 w-4 text-accent shrink-0" /> 更低费率和折扣</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-primary-foreground/20"><Check className="h-4 w-4 text-accent shrink-0" /> 优先排期</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-primary-foreground/20"><Check className="h-4 w-4 text-accent shrink-0" /> 始终保持干净的家</li>
              <li className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-accent shrink-0" /> 最佳长期价值</li>
            </ul>
            <Button asChild className="w-full rounded-xl bg-background text-primary hover:bg-secondary shadow-lg btn-pulse">
              <a href={`${BOOKING_URL}?view=custom-plan`}>
                <Zap className="mr-2 h-4 w-4 fill-primary/20" />
                开始节省 20-35%
              </a>
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full mt-4 text-xs text-center text-primary-foreground/80 hover:text-white transition-colors flex items-center justify-center gap-1">
                  查看频率折扣 <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-4 border-border shadow-xl" side="top" align="center">
                <div className="text-[11px] font-bold text-center mb-3 text-primary tracking-wider uppercase">定期清洁优惠</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">每周</span>
                    <span className="font-bold text-primary text-xs">优惠 35%</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">每两周</span>
                    <span className="font-bold text-primary text-xs">优惠 30%</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">每三周</span>
                    <span className="font-bold text-primary text-xs">优惠 25%</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">每月</span>
                    <span className="font-bold text-primary text-xs">优惠 20%</span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

        </div>

        {/* RECOMMENDATION ENGINE */}
        <ChineseServiceRecommendationEngine />

        {/* SPECIALIZED SERVICES */}
        <div className={cn("bg-white dark:bg-white border border-border dark:border-slate-200 rounded-3xl p-10 shadow-sm anim-fade-up delay-600", sectionVisible && "is-visible")}>
          <div className="text-primary dark:text-slate-900 font-bold tracking-widest uppercase text-xs mb-2">专业服务</div>
          <h3 className="text-3xl font-bold mb-4 dark:text-slate-900">需要更具体的服务？</h3>
          <p className="text-muted-foreground dark:text-slate-600 mb-10 max-w-2xl">
            除了核心住宅服务外，我们还提供特殊情况的专业清洁。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-50 dark:bg-slate-50 border border-border dark:border-slate-200 rounded-2xl p-6 flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
              <h4 className="text-xl font-bold mb-1 dark:text-slate-900">装修后清洁</h4>
              <p className="text-sm text-muted-foreground dark:text-slate-600 mb-4">灰尘、碎屑和翻新清理</p>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm dark:text-slate-900"><Check className="h-3 w-3 text-primary dark:text-slate-900 shrink-0" /> 细尘清除</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-900"><Check className="h-3 w-3 text-primary dark:text-slate-900 shrink-0" /> 表面细节处理</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-900"><Check className="h-3 w-3 text-primary dark:text-slate-900 shrink-0" /> 地板和固定装置</li>
              </ul>
              <Button asChild variant="outline" className="w-full rounded-xl border-border dark:border-slate-200 hover:bg-secondary dark:text-slate-900">
                <a href="/zh/post-construction">
                  <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-slate-900/20" />
                  获取即时报价
                </a>
              </Button>
            </div>

            <div className="bg-slate-50 dark:bg-slate-50 border border-border dark:border-slate-200 rounded-2xl p-6 flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
              <h4 className="text-xl font-bold mb-1 dark:text-slate-900">办公室清洁</h4>
              <p className="text-sm text-muted-foreground dark:text-slate-600 mb-4">可靠的商业维护</p>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm dark:text-slate-900"><Check className="h-3 w-3 text-primary dark:text-slate-900 shrink-0" /> 办公桌和公共区域</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-900"><Check className="h-3 w-3 text-primary dark:text-slate-900 shrink-0" /> 洗手间和厨房</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-900"><Check className="h-3 w-3 text-primary dark:text-slate-900 shrink-0" /> 非工作时间选项</li>
              </ul>
              <Button asChild variant="outline" className="w-full rounded-xl border-border dark:border-slate-200 hover:bg-secondary dark:text-slate-900">
                <a href="/zh/office-cleaning">
                  <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-slate-900/20" />
                  获取商业报价
                </a>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
